import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { erroDaApi, formatarCentavos, type AmbienteRepasse } from "@/lib/repasse-externo";
import { invalidarRepasses } from "./RegistrarRepasseExternoDialog";
import { ComprovanteRepasseButton } from "./ComprovanteRepasseButton";

export interface RegistroRepasseExterno {
  id: string;
  modalidade: "integral" | "parcial";
  valorCents: number;
  observacao: string | null;
  comprovanteNome: string | null;
  status: "ativo" | "desfeito";
  registradoEm: string;
  registradoPorNome: string | null;
  desfeitoEm: string | null;
  desfeitoPorNome: string | null;
}

export interface AlvoHistorico {
  vistoriadoraId: string;
  vistoriadoraNome: string;
  ambiente: AmbienteRepasse;
}

const dataHora = (iso: string) => new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });

export function HistoricoRepasseExternoDialog({ alvo, onClose }: { alvo: AlvoHistorico | null; onClose: () => void }) {
  const { toast } = useToast();
  const [desfazerAlvo, setDesfazerAlvo] = useState<RegistroRepasseExterno | null>(null);
  const historico = useQuery<{ registros: RegistroRepasseExterno[] }>({
    queryKey: ["/api/owner/repasses/externo", alvo?.vistoriadoraId, alvo?.ambiente],
    queryFn: async () =>
      (await apiRequest("GET", `/api/owner/repasses/externo?vistoriadoraId=${alvo!.vistoriadoraId}&ambiente=${alvo!.ambiente}`)).json(),
    enabled: !!alvo,
  });
  const desfazer = useMutation({
    mutationFn: async (r: RegistroRepasseExterno) =>
      (await apiRequest("POST", `/api/owner/repasses/externo/${r.id}/desfazer`, { confirmar: true })).json(),
    onSuccess: (_, r) => {
      if (alvo) invalidarRepasses(alvo);
      setDesfazerAlvo(null);
      toast({ title: `Registro desfeito: ${formatarCentavos(r.valorCents)} voltou para "A pagar"` });
    },
    onError: (e) => {
      if (alvo) invalidarRepasses(alvo);
      setDesfazerAlvo(null);
      toast({ title: "Não foi possível desfazer", description: erroDaApi(e).message, variant: "destructive" });
    },
  });
  const registros = historico.data?.registros ?? [];

  return (
    <>
      <Dialog open={!!alvo} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Histórico de repasses fora da plataforma</DialogTitle>
            <DialogDescription>{alvo?.vistoriadoraNome} · {alvo?.ambiente}</DialogDescription>
          </DialogHeader>
          {historico.isLoading ? (
            <div className="space-y-2"><Skeleton className="h-8" /><Skeleton className="h-8" /></div>
          ) : historico.isError ? (
            <p role="alert" className="text-sm text-red-700">{erroDaApi(historico.error).message}</p>
          ) : !registros.length ? (
            <p className="py-6 text-center text-sm text-slate-500">Nenhum repasse fora da plataforma registrado.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead><TableHead>Modalidade</TableHead><TableHead className="text-right">Valor</TableHead>
                    <TableHead>Observação</TableHead><TableHead>Comprovante</TableHead><TableHead>Registrado por</TableHead><TableHead>Status</TableHead>
                    <TableHead className="text-right">Ação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {registros.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="whitespace-nowrap">{dataHora(r.registradoEm)}</TableCell>
                      <TableCell>{r.modalidade === "integral" ? "Integral" : "Parcial"}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatarCentavos(r.valorCents)}</TableCell>
                      <TableCell className="max-w-[16rem] truncate" title={r.observacao ?? ""}>{r.observacao ?? "—"}</TableCell>
                      <TableCell>{r.comprovanteNome ? <ComprovanteRepasseButton registroId={r.id} nome={r.comprovanteNome} /> : <span className="text-sm text-muted-foreground">Sem comprovante</span>}</TableCell>
                      <TableCell>{r.registradoPorNome ?? "—"}</TableCell>
                      <TableCell>
                        {r.status === "ativo" ? (
                          <Badge>Ativo</Badge>
                        ) : (
                          <Badge variant="secondary" title={r.desfeitoEm ? `Desfeito em ${dataHora(r.desfeitoEm)} por ${r.desfeitoPorNome ?? "—"}` : undefined}>
                            Desfeito
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {r.status === "ativo" && <Button size="sm" variant="outline" onClick={() => setDesfazerAlvo(r)}>Desfazer</Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!desfazerAlvo} onOpenChange={(open) => !open && setDesfazerAlvo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desfazer o registro de {desfazerAlvo ? formatarCentavos(desfazerAlvo.valorCents) : ""}?</AlertDialogTitle>
            <AlertDialogDescription>O valor volta para "A pagar". O registro continua no histórico como desfeito.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction onClick={() => desfazerAlvo && desfazer.mutate(desfazerAlvo)} disabled={desfazer.isPending}>
              Desfazer registro
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
